import { find } from 'lodash';
import { createSelector } from 'reselect';
import { getCanvasSizeFromDesignArea } from '@configuratorware/configurator-frontendgui/App/Reducers/DesignArea/Selectors';
import { getItemAttributeValueByIdentifier } from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/AttributeSelectors';
import { OptionClassificationIdentifiers } from '../../Enums';
import {
  buildDefaultDetailImagePath,
  getOptionDetailImagePath,
  resolveOptionDetailImageUrl,
} from '../../utils/backgroundImage';
import {
  DEFAULT_CANVAS_HEIGHT_MM,
  DEFAULT_CANVAS_WIDTH_MM,
  normalizeCanvasSize,
} from '../../utils/editorUnits';
import { computeDesignSize } from '../../utils/designSize';
import {
  getOptionClassification,
  getSelectedOptionFromClassification,
} from '../../utils/optionClassifications';
import { createInitialEditorState } from './types';
import type { EditorState } from './types';

const getConfigurator = (state) => state.configurator;

export const getEditorState = createSelector(getConfigurator, (configurator): EditorState => {
  return configurator?.configuration?.customdata?.editor || createInitialEditorState();
});

export const getEditorObjects = createSelector(getEditorState, (editor) => editor.objects);

export const getEditorSelectedId = createSelector(getEditorState, (editor) => editor.selectedId);

export const getEditorDesignSize = createSelector(getEditorObjects, (objects) => computeDesignSize(objects));

export const getEditorAddMode = createSelector(getEditorState, (editor) => editor.addMode);

export const getSelectedEditorItem = createSelector(getEditorState, (editor) =>
  editor.selectedId ? editor.objects[editor.selectedId] : null
);

export const getBlockedMaterialId = createSelector(
  getConfigurator,
  (configurator): string | null => configurator?.blockedMaterialId ?? null
);

export const getOptionsList = createSelector(
  getConfigurator,
  (configurator) => configurator.optionsList || {}
);

export const getOptionsForClassification = (identifier: string) =>
  createSelector(getOptionsList, (optionsList) => optionsList[identifier] || []);

export const getOptionClassificationById = (identifier: string) =>
  createSelector(getConfigurator, (configurator) =>
    getOptionClassification(configurator?.configuration?.optionclassifications, identifier)
  );

export const getSelectedOptionForClassification = (identifier: string) =>
  createSelector(getConfigurator, (configurator) =>
    getSelectedOptionFromClassification(configurator?.configuration?.optionclassifications, identifier)
  );

export const getResolvedBackgroundOption = createSelector(
  getConfigurator,
  getOptionsForClassification(OptionClassificationIdentifiers.background),
  (configurator, backgroundOptions) => {
    const classificationId = OptionClassificationIdentifiers.background;
    const selected = getSelectedOptionFromClassification(
      configurator?.configuration?.optionclassifications,
      classificationId
    );
    if (!selected?.identifier) return null;

    const fromList = backgroundOptions.find((option) => option.identifier === selected.identifier);
    const fromStateOc =
      configurator?.selectedoptionclassification?.identifier === classificationId
        ? configurator.selectedoptionclassification?.selectedoptions?.[0]
        : null;
    const fromState = fromStateOc?.identifier === selected.identifier ? fromStateOc : null;

    const merged = {
      ...fromList,
      ...fromState,
      ...selected,
    };

    const detailImagePath =
      getOptionDetailImagePath(selected) ||
      getOptionDetailImagePath(fromState) ||
      getOptionDetailImagePath(fromList) ||
      (selected.identifier ? buildDefaultDetailImagePath(selected.identifier) : null);

    return detailImagePath ? { ...merged, detailImage: detailImagePath } : merged;
  }
);

export const getResolvedBackgroundDetailImageUrl = createSelector(getResolvedBackgroundOption, (option) =>
  resolveOptionDetailImageUrl(option)
);

export const getDefaultCanvasSize = (state) => {
  const defaultArea = find(state.designArea?.filteredDesignAreas, { identifier: 'default' });
  if (!defaultArea) {
    return { width: DEFAULT_CANVAS_WIDTH_MM, height: DEFAULT_CANVAS_HEIGHT_MM, dpi: 300 };
  }
  return normalizeCanvasSize(getCanvasSizeFromDesignArea(state, defaultArea));
};

export const getOptionClassificationTitle = (identifier: string) =>
  createSelector(getConfigurator, (configurator) => {
    const oc = getOptionClassification(configurator?.configuration?.optionclassifications, identifier);
    return oc?.title || identifier;
  });

export const getDefaultColorHex = createSelector(
  getSelectedOptionForClassification(OptionClassificationIdentifiers.color),
  (option) => {
    if (!option) return '#000000';
    const hex = getItemAttributeValueByIdentifier(option, 'hex', '000000');
    return hex.startsWith('#') ? hex : `#${hex}`;
  }
);

export const getDefaultColorOptionId = createSelector(
  getSelectedOptionForClassification(OptionClassificationIdentifiers.color),
  (option) => option?.identifier ?? null
);

export const getFonts = (state) => state.fonts || [];

export { OptionClassificationIdentifiers, getItemAttributeValueByIdentifier };
